// react
import type { ChangeEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute } from '@tanstack/react-router';

// material ui icons
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

// material ui
import {
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import { useGetBuildingsByAccountQuery } from '@/api/recalc/endpoints/buildingApiSlice';
import {
  useGetSubscriptionsByAccountQuery,
  useUpdateManualRunRequestMutation,
} from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import {
  useDeleteOccupancyEventMutation,
  useGetOccupancyEventsQuery,
  usePostOccupancyEventMutation,
  usePutOccupancyEventMutation,
} from '@/api/recalc/endpoints/occupancyApiSlice';

// shared components
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import { EventDetails } from '@/features/calendar/components/EventDetails';
// relative
import '@/features/calendar/components/styles.css';
import StaticDateRangeNavigator from '@/features/calendar/components/StaticDateRangeNavigator';
import TargetsSelector from '@/features/calendar/components/TargetsSelector';
import ViewFilterSelector from '@/features/calendar/components/ViewFilterSelector';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

import bootstrapPlugin from '@fullcalendar/bootstrap';
import type {
  DateSelectArg,
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventInput,
  EventMountArg,
} from '@fullcalendar/core';
import daLocale from '@fullcalendar/core/locales/da';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { DateTime, type Zone } from 'luxon';

const STEP_SECONDS = 1800; // 30 minutes

type JobSubscriptionId = number;
type BuildingId = number;
type OccupancyEventId = number;
type EventScope = 'subscription' | 'building';
type OccupancyType = 'occupied' | 'empty';
type ApiOccupancyState = 'Occupied' | 'Empty';

interface BuildingWithSubs {
  buildingId: BuildingId;
  buildingName: string;
  subs: Array<{ id: JobSubscriptionId; name: string }>;
}

interface TargetSelectionState {
  buildings: Set<BuildingId>;
  subs: Set<JobSubscriptionId>;
}

interface ViewFilterState extends TargetSelectionState {
  allInitialized: boolean;
}

interface SelectedEventState {
  id: string | null;
  title: string;
  start: string;
  end: string;
  type: OccupancyType;
  scope: EventScope;
  occupancyEventId: OccupancyEventId | null;
  buildingId: BuildingId | null;
  buildingName: string;
  jobSubscriptionId: JobSubscriptionId | null;
  subscriptionDisplayName: string;
  eventName: string;
  affectedSubscriptions: JobSubscriptionId[];
}

interface CalendarExtendedProps {
  scope: EventScope;
  occupancyEventId: OccupancyEventId;
  occupancyState: ApiOccupancyState;
  jobSubscriptionId: JobSubscriptionId | null;
  buildingId: BuildingId | null;
  subscriptionDisplayName: string | null;
  buildingName: string;
  eventName: string;
  affectedSubscriptions: JobSubscriptionId[];
}

interface ApiSubscription {
  JobSubscriptionId: JobSubscriptionId;
  JobType?: string | null;
  DisplayName?: string | null;
}

interface ApiBuilding {
  BuildingId: BuildingId;
  Name?: string | null;
  JobSubscriptions?: JobSubscriptionId[];
}

interface ApiOccupancyEvent {
  OccupancyId: OccupancyEventId;
  Name?: string | null;
  Start: string;
  End: string;
  OccupancyState: ApiOccupancyState;
  JobSubscriptionId?: JobSubscriptionId | null;
  BuildingId?: BuildingId | null;
}

type CalendarEventInput = EventInput & {
  extendedProps: CalendarExtendedProps;
};

const createInitialSelectedEvent = (): SelectedEventState => ({
  id: null,
  title: '',
  start: new Date().toISOString(),
  end: new Date().toISOString(),
  type: 'occupied',
  scope: 'subscription',
  occupancyEventId: null,
  buildingId: null,
  buildingName: '',
  jobSubscriptionId: null,
  subscriptionDisplayName: '',
  eventName: '',
  affectedSubscriptions: [],
});

// Snap any DateTime to the nearest STEP_SECONDS
function roundToStep(dt: DateTime, stepSeconds = STEP_SECONDS): DateTime {
  const secs = Math.round(dt.toSeconds() / stepSeconds) * stepSeconds;
  return DateTime.fromSeconds(secs, { zone: dt.zone });
}

// Combine date ('yyyy-MM-dd') and time ('HH:mm') into a DateTime in the given zone
function mergeDateAndTime(
  dateStr: string,
  timeStr: string,
  zone: string | Zone,
): DateTime {
  const date = DateTime.fromFormat(dateStr, 'yyyy-LL-dd', { zone });
  const [h = '00', m = '00'] = (timeStr || '').split(':');
  return date.set({
    hour: Number(h),
    minute: Number(m),
    second: 0,
    millisecond: 0,
  });
}

export const Route = createFileRoute(
  '/$accountId/_user/predictive-heat-control/calendar',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: {
    crumb: 'buildingOptimization:predictiveHeatControlCalendar.title',
  },
});

function RouteComponent() {
  const { t } = useTranslation('calendar');
  const account = useSelector(getSelectedAccount);
  const calendarRef = useRef<FullCalendar | null>(null);

  // =========================
  // Create targets (POST)
  // =========================
  const [targetSelections, setTargetSelections] =
    useState<TargetSelectionState>({
      buildings: new Set(),
      subs: new Set(),
    });

  const toggleBuilding = (building: BuildingWithSubs) => {
    setTargetSelections((prev) => {
      const buildings = new Set(prev.buildings);
      const subs = new Set(prev.subs);
      if (buildings.has(building.buildingId)) {
        buildings.delete(building.buildingId);
      } else {
        buildings.add(building.buildingId);
        building.subs.forEach((s) => subs.delete(s.id));
      }
      return { buildings, subs };
    });
  };

  const toggleSub = (building: BuildingWithSubs, subId: JobSubscriptionId) => {
    setTargetSelections((prev) => {
      const buildings = new Set(prev.buildings);
      const subs = new Set(prev.subs);

      if (buildings.has(building.buildingId)) {
        buildings.delete(building.buildingId);
      }

      if (subs.has(subId)) subs.delete(subId);
      else subs.add(subId);

      const allSubIds = building.subs.map((s) => s.id);
      const allChecked = allSubIds.every((id) => subs.has(id));
      if (allChecked) {
        allSubIds.forEach((id) => subs.delete(id));
        buildings.add(building.buildingId);
      }

      return { buildings, subs };
    });
  };

  // =========================
  // Data
  // =========================
  const {
    data: subscriptions,
    isSuccess: isSuccessSubscriptions,
    isLoading: isLoadingSubscriptions,
    isFetching: isFetchingSubscriptions,
  } = useGetSubscriptionsByAccountQuery(account?.AccountId, {
    skip: !account?.AccountId,
  });

  const {
    data: buildings,
    isSuccess: isSuccessBuildings,
    isLoading: isLoadingBuildings,
    isFetching: isFetchingBuildings,
  } = useGetBuildingsByAccountQuery(account?.AccountId, {
    skip: !account?.AccountId,
  });

  const {
    data: occupancyEvents,
    isLoading: isLoadingOccupancyEvents,
    isFetching: isFetchingOccupancyEvents,
    isSuccess: isSuccessOccupancyEvents,
  } = useGetOccupancyEventsQuery(undefined, {
    skip: !account?.AccountId,
  });

  const [postOccupancyEvent] = usePostOccupancyEventMutation();
  const [deleteOccupancyEvent] = useDeleteOccupancyEventMutation();
  const [putOccupancyEvent] = usePutOccupancyEventMutation();

  const subscriptionsList = useMemo<ApiSubscription[]>(
    () => subscriptions ?? [],
    [subscriptions],
  );
  const buildingsList = useMemo<ApiBuilding[]>(
    () => buildings ?? [],
    [buildings],
  );
  const occupancyEventsList = useMemo<ApiOccupancyEvent[]>(
    () => occupancyEvents ?? [],
    [occupancyEvents],
  );

  // =========================
  // View filter (SHOW/HIDE)
  // =========================
  const [viewFilter, setViewFilter] = useState<ViewFilterState>({
    buildings: new Set(),
    subs: new Set(),
    allInitialized: false,
  });

  const toggleViewBuilding = (building: BuildingWithSubs) => {
    setViewFilter((prev) => {
      const buildings = new Set(prev.buildings);
      const subs = new Set(prev.subs);
      if (buildings.has(building.buildingId)) {
        buildings.delete(building.buildingId);
      } else {
        buildings.add(building.buildingId);
      }
      return { ...prev, buildings, subs };
    });
  };

  const toggleViewSub = (
    _building: BuildingWithSubs,
    subId: JobSubscriptionId,
  ) => {
    setViewFilter((prev) => {
      const buildings = new Set(prev.buildings);
      const subs = new Set(prev.subs);
      if (subs.has(subId)) subs.delete(subId);
      else subs.add(subId);
      return { ...prev, buildings, subs };
    });
  };

  const initializedViewFilter = useMemo(() => {
    const hasUserCustomized =
      viewFilter.buildings.size > 0 || viewFilter.subs.size > 0;
    if (isSuccessBuildings && buildingsList.length > 0) {
      if (!hasUserCustomized) {
        return {
          buildings: new Set(buildingsList.map((b) => b.BuildingId)),
          subs: new Set(),
          allInitialized: true,
        };
      }
      return { ...viewFilter, allInitialized: true };
    }
    return viewFilter;
  }, [viewFilter, isSuccessBuildings, buildingsList]);

  // ---------- maps for labels ----------
  const subsById = useMemo(() => {
    const m = new Map<JobSubscriptionId, ApiSubscription>();
    if (isSuccessSubscriptions) {
      subscriptionsList.forEach((s) => m.set(s.JobSubscriptionId, s));
    }
    return m;
  }, [isSuccessSubscriptions, subscriptionsList]);

  const buildingBySubId = useMemo(() => {
    const m = new Map<JobSubscriptionId, ApiBuilding>();
    if (isSuccessBuildings) {
      buildingsList.forEach((b) =>
        (b.JobSubscriptions || []).forEach((id) => m.set(id, b)),
      );
    }
    return m;
  }, [isSuccessBuildings, buildingsList]);

  const predictiveSubscriptionIds = useMemo(() => {
    const set = new Set<JobSubscriptionId>();
    if (isSuccessSubscriptions) {
      subscriptionsList
        .filter((s) => s.JobType === 'PredictiveHeatControl')
        .forEach((s) => set.add(s.JobSubscriptionId));
    }
    return set;
  }, [isSuccessSubscriptions, subscriptionsList]);

  // PCH subs per building
  const pchSubsByBuildingId = useMemo(() => {
    const m = new Map<BuildingId, JobSubscriptionId[]>();
    if (
      !isSuccessBuildings ||
      !isSuccessSubscriptions ||
      buildingsList.length === 0 ||
      subscriptionsList.length === 0
    )
      return m;
    const pchSet = new Set(
      subscriptionsList
        .filter((s) => s.JobType === 'PredictiveHeatControl')
        .map((s) => s.JobSubscriptionId),
    );
    buildingsList.forEach((b) => {
      const subs = (b.JobSubscriptions || []).filter((id) => pchSet.has(id));
      m.set(b.BuildingId, subs);
    });
    return m;
  }, [
    isSuccessBuildings,
    isSuccessSubscriptions,
    buildingsList,
    subscriptionsList,
  ]);

  // ---------- normalize events (subscription + building scope) ----------
  const normalizedEvents = useMemo(() => {
    if (!isSuccessOccupancyEvents || occupancyEventsList.length === 0)
      return [];

    const result: CalendarEventInput[] = [];

    for (const e of occupancyEventsList) {
      const hasSub = e.JobSubscriptionId != null;
      const hasBuilding = e.BuildingId != null;

      if (hasSub) {
        if (!predictiveSubscriptionIds.has(e.JobSubscriptionId)) continue;

        const sub = subsById.get(e.JobSubscriptionId);
        const bld = buildingBySubId.get(e.JobSubscriptionId);

        result.push({
          id: String(e.OccupancyId),
          title: e.Name || 'Ingen titel',
          start: e.Start,
          end: e.End,
          color: e.OccupancyState === 'Occupied' ? '#96b100a9' : '#ff5a39a9',
          extendedProps: {
            scope: 'subscription',
            occupancyEventId: e.OccupancyId,
            occupancyState: e.OccupancyState,
            jobSubscriptionId: e.JobSubscriptionId,
            buildingId: bld?.BuildingId ?? null,
            subscriptionDisplayName:
              sub?.DisplayName ?? '(ukendt blandesløjfe)',
            buildingName: bld?.Name ?? '(ukendt bygning)',
            eventName: e.Name || 'Ingen titel',
            affectedSubscriptions: [e.JobSubscriptionId],
          },
        });
      } else if (hasBuilding) {
        const affected = pchSubsByBuildingId.get(e.BuildingId) || [];
        if (affected.length === 0) continue;
        const bld = buildingsList.find((b) => b.BuildingId === e.BuildingId);

        result.push({
          id: `b-${e.OccupancyId}`,
          title: e.Name || 'Ingen titel',
          start: e.Start,
          end: e.End,
          color: e.OccupancyState === 'Occupied' ? '#96b100a9' : '#ff5a39a9',
          extendedProps: {
            scope: 'building',
            occupancyEventId: e.OccupancyId,
            occupancyState: e.OccupancyState,
            buildingId: e.BuildingId,
            buildingName: bld?.Name ?? `(Bygning ${e.BuildingId})`,
            jobSubscriptionId: null,
            subscriptionDisplayName: null,
            eventName: e.Name || 'Ingen titel',
            affectedSubscriptions: affected,
          },
        });
      }
    }

    return result;
  }, [
    isSuccessOccupancyEvents,
    occupancyEventsList,
    predictiveSubscriptionIds,
    subsById,
    buildingBySubId,
    pchSubsByBuildingId,
    buildingsList,
  ]);

  // ---------- apply VIEW filter ----------
  const visibleEvents = useMemo(() => {
    if (!initializedViewFilter.allInitialized) return normalizedEvents;

    return normalizedEvents.filter((ev) => {
      const xp = ev.extendedProps;
      if (xp.scope === 'subscription') {
        return (
          (xp.jobSubscriptionId &&
            initializedViewFilter.subs.has(xp.jobSubscriptionId)) ||
          (xp.buildingId && initializedViewFilter.buildings.has(xp.buildingId))
        );
      }
      if (xp.scope === 'building') {
        const buildingShown =
          xp.buildingId && initializedViewFilter.buildings.has(xp.buildingId);
        if (buildingShown) return true;
        if (Array.isArray(xp.affectedSubscriptions)) {
          return xp.affectedSubscriptions.some((sid) =>
            initializedViewFilter.subs.has(sid),
          );
        }
        return false;
      }
      return true;
    });
  }, [normalizedEvents, initializedViewFilter]);

  // ---------- event element registry ----------
  const eventElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const handleEventDidMount = (arg: EventMountArg) => {
    eventElsRef.current.set(arg.event.id, arg.el);

    // Pseudo events: leave default
    if (
      String(arg.event.id).startsWith('pseudo-') ||
      arg.event.classNames?.includes('pseudo-event')
    )
      return;

    const xp = arg.event.extendedProps as CalendarExtendedProps;
    const occupancy = xp.occupancyState; // "Empty" | "Occupied"
    const scope = xp.scope; // "subscription" | "building"
    const el = arg.el;

    const resetBorders = () => {
      el.style.borderLeftWidth = '8px';
      el.style.borderLeftStyle = 'solid';
      el.style.borderLeftColor = 'transparent';
      el.style.borderImage = '';
      el.style.borderImageSlice = '';
    };

    const applyAccent = () => {
      if (scope === 'subscription') {
        // dashed/gradient for subs
        if (occupancy === 'Empty') {
          el.style.borderLeftStyle = 'solid';
          el.style.borderLeftColor = 'transparent';
          el.style.borderImage =
            'repeating-linear-gradient(45deg, #ff5a39, #ff5a39 4px, transparent 4px, transparent 8px) 1';
          el.style.borderImageSlice = '1';
        } else if (occupancy === 'Occupied') {
          el.style.borderLeftStyle = 'solid';
          el.style.borderLeftColor = 'transparent';
          el.style.borderImage =
            'repeating-linear-gradient(45deg, #96b100, #96b100 4px, transparent 4px, transparent 8px) 1';
          el.style.borderImageSlice = '1';
        }
      } else if (scope === 'building') {
        // solid for buildings: red when Empty, green when Occupied
        el.style.borderLeftStyle = 'solid';
        el.style.borderLeftColor =
          occupancy === 'Empty' ? '#ff5a39' : '#96b100';
        el.style.borderImage = '';
        el.style.borderImageSlice = '';
      }
    };

    const isMonthDot =
      arg.view?.type === 'dayGridMonth' &&
      el.classList.contains('fc-daygrid-dot-event');

    if (isMonthDot) {
      el.classList.add('month-bar'); // your CSS turns dot into bar
      resetBorders();
      applyAccent();

      // Optional month bar colors
      el.style.backgroundColor =
        occupancy === 'Occupied' ? '#96b100a1' : '#ff5a39a1';
      el.style.setProperty('--fc-event-text-color', '#1a1a1a');
      el.style.color = '#ffffffff';
      return;
    }

    // Non-month/block events
    resetBorders();
    applyAccent();
  };

  const handleEventWillUnmount = (arg: EventMountArg) => {
    eventElsRef.current.delete(arg.event.id);
  };

  // ---------- dialog state ----------
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isEventClick, setIsEventClick] = useState(false);
  const [pseudoEventId, setPseudoEventId] = useState<string | null>(null);

  const [selectedEvent, setSelectedEvent] = useState<SelectedEventState>(
    createInitialSelectedEvent,
  );

  // ---- select to create ----
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    setIsEventClick(false);
    const api = calendarRef.current?.getApi();
    if (!api) return;

    if (pseudoEventId) {
      const old = api.getEventById(pseudoEventId);
      if (old) old.remove();
      setPseudoEventId(null);
    }

    const tempId = `pseudo-${Date.now()}`;
    const tempEvent: EventInput = {
      id: tempId,
      title: '',
      start: selectInfo.startStr,
      end: selectInfo.endStr,
      allDay: selectInfo.allDay,
      classNames: ['pseudo-event'],
    };
    api.addEvent(tempEvent);
    api.unselect();
    setPseudoEventId(tempId);

    setSelectedEvent((prev) => ({
      ...prev,
      ...createInitialSelectedEvent(),
      start: selectInfo.startStr,
      end: selectInfo.endStr,
    }));

    requestAnimationFrame(() => {
      const el = eventElsRef.current.get(tempId);
      if (el) setAnchorEl(el);
    });
  };

  // ---- click existing event ----
  const handleExistingEventClick = (clickInfo: EventClickArg) => {
    setIsEventClick(true);
    const ev = clickInfo.event;
    const xp = ev.extendedProps as CalendarExtendedProps;

    setSelectedEvent({
      id: ev.id,
      title: ev.title,
      start: ev.start?.toISOString(),
      end: ev.end?.toISOString(),
      type: xp.occupancyState === 'Occupied' ? 'occupied' : 'empty',
      scope: xp.scope,
      occupancyEventId: xp.occupancyEventId ?? null,
      buildingId: xp.buildingId ?? null,
      buildingName: xp.buildingName ?? '',
      jobSubscriptionId: xp.jobSubscriptionId ?? null,
      subscriptionDisplayName: xp.subscriptionDisplayName ?? '',
      eventName: xp.eventName ?? ev.title,
      affectedSubscriptions: Array.isArray(xp.affectedSubscriptions)
        ? xp.affectedSubscriptions
        : [],
    });

    setAnchorEl(clickInfo.el);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    const api = calendarRef.current?.getApi();
    if (pseudoEventId && api) {
      const tmp = api.getEventById(pseudoEventId);
      if (tmp) tmp.remove();
      setPseudoEventId(null);
    }
    setSelectedEvent(createInitialSelectedEvent());
  };

  // ---- edit fields + validation ----
  const handleTimeChange = (
    field: keyof Pick<SelectedEventState, 'start' | 'end'>,
    value: string,
  ) => {
    setSelectedEvent((prev) => ({ ...prev, [field]: value }));
  };

  const validateAndSetEventTimes = () => {
    setSelectedEvent((prev) => {
      let s = DateTime.fromISO(prev.start);
      let e = DateTime.fromISO(prev.end);
      if (!s.isValid) s = DateTime.now();
      if (!e.isValid) e = s.plus({ minutes: 15 });
      if (s > e) e = s.plus({ minutes: 15 });
      return { ...prev, start: s.toISO(), end: e.toISO() };
    });
  };

  const handleTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextType: OccupancyType =
      e.target.value === 'occupied' ? 'occupied' : 'empty';
    setSelectedEvent((prev) => ({ ...prev, type: nextType }));
  };

  const toApiOccupancyState = (t: OccupancyType): ApiOccupancyState =>
    t === 'occupied' ? 'Occupied' : 'Empty';

  const [manualRunRequest] = useUpdateManualRunRequestMutation();

  const handleManualRunRequest = ({
    now,
    eventStart,
    buildingTargets = [],
    subTargets = [],
  }: {
    now: DateTime;
    eventStart: DateTime;
    buildingTargets?: BuildingId[];
    subTargets?: JobSubscriptionId[];
  }) => {
    const isEventToday = now && eventStart && eventStart.hasSame(now, 'day');
    if (!isEventToday) return;
    buildingTargets.forEach((BuildingId) => {
      const building = buildingsList.find((b) => b.BuildingId === BuildingId);
      (building?.JobSubscriptions || [])
        .filter((id) => predictiveSubscriptionIds.has(id))
        .forEach((subscriptionId) => {
          manualRunRequest({ id: subscriptionId });
        });
    });
    subTargets.forEach((JobSubscriptionId) => {
      manualRunRequest({ id: JobSubscriptionId });
    });
  };

  // ---- CREATE ----
  const handleCreate = async () => {
    const base = {
      Name: selectedEvent.title?.trim() || null,
      Start: selectedEvent.start,
      End: selectedEvent.end,
      OccupancyState: toApiOccupancyState(selectedEvent.type),
    };

    const buildingTargets = Array.from(targetSelections.buildings);
    const subTargets = Array.from(targetSelections.subs);

    if (buildingTargets.length === 0 && subTargets.length === 0) {
      console.warn('No targets selected.');
      return;
    }

    const requests = [
      ...buildingTargets.map((BuildingId) => ({ ...base, BuildingId })),
      ...subTargets.map((JobSubscriptionId) => ({
        ...base,
        JobSubscriptionId,
      })),
    ];

    try {
      await Promise.allSettled(
        requests.map((payload) => postOccupancyEvent(payload).unwrap()),
      );

      // clean up
      const api = calendarRef.current?.getApi();
      if (pseudoEventId && api) {
        const tmp = api.getEventById(pseudoEventId);
        if (tmp) tmp.remove();
      }
      setPseudoEventId(null);
      setAnchorEl(null);
      setTargetSelections({ buildings: new Set(), subs: new Set() });
    } catch (err) {
      console.error('Unexpected failure creating events:', err);
    }
    handleManualRunRequest({
      now: DateTime.now(),
      eventStart: DateTime.fromISO(selectedEvent.start),
      buildingTargets,
      subTargets,
    });
  };

  // ---- UPDATE (PUT) ----
  // Your API expects: body { Start, End, OccupancyState, Name } + event id.
  const handleUpdate = async () => {
    if (!selectedEvent.occupancyEventId) return;

    const body = {
      Name: selectedEvent.title?.trim() || null,
      Start: selectedEvent.start, // ISO string
      End: selectedEvent.end, // ISO string
      OccupancyState: toApiOccupancyState(selectedEvent.type), // "Occupied" | "Empty"
    };

    try {
      // NOTE: spread `body` at the top level, do NOT nest under `body`
      await putOccupancyEvent({
        id: selectedEvent.occupancyEventId,
        ...body,
      }).unwrap();

      setAnchorEl(null);
    } catch (err) {
      console.error('Failed to update event:', err);
    }
    handleManualRunRequest({
      now: DateTime.now(),
      eventStart: DateTime.fromISO(selectedEvent.start),
      buildingTargets: [],
      subTargets: selectedEvent.affectedSubscriptions,
    });
  };

  // ---- DELETE ----
  const handleDelete = async () => {
    if (!selectedEvent.occupancyEventId) return;

    try {
      await deleteOccupancyEvent(selectedEvent.occupancyEventId).unwrap();
      // optimistic remove
      const api = calendarRef.current?.getApi();
      const ev = selectedEvent.id ? api?.getEventById(selectedEvent.id) : null;
      if (ev) ev.remove();
      setAnchorEl(null);
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
    handleManualRunRequest({
      now: DateTime.now(),
      eventStart: DateTime.fromISO(selectedEvent.start),
      buildingTargets: [],
      subTargets: selectedEvent.affectedSubscriptions,
    });
  };

  const handleTodayButtonClick = () => {
    calendarRef.current?.getApi()?.today();
  };

  // Build list for Create + View UIs
  const buildingsWithSubs = useMemo(() => {
    if (
      !isSuccessBuildings ||
      !isSuccessSubscriptions ||
      buildingsList.length === 0 ||
      subscriptionsList.length === 0
    )
      return [];

    const subMap = new Map(
      subscriptionsList.map((s) => [s.JobSubscriptionId, s]),
    );
    return buildingsList.map((b) => {
      const subs = (b.JobSubscriptions || [])
        .map((id) => subMap.get(id))
        .filter(Boolean)
        .filter((s) => s.JobType === 'PredictiveHeatControl')
        .map((s) => ({
          id: s.JobSubscriptionId,
          name: s.DisplayName || `Subscription ${s.JobSubscriptionId}`,
        }));

      return {
        buildingId: b.BuildingId,
        buildingName: b.Name || `Building ${b.BuildingId}`,
        subs,
      };
    });
  }, [
    isSuccessBuildings,
    isSuccessSubscriptions,
    buildingsList,
    subscriptionsList,
  ]);

  // ========= Date & Time (Outlook-like) =========
  const startDT = useMemo(
    () => DateTime.fromISO(selectedEvent.start),
    [selectedEvent.start],
  );
  const endDT = useMemo(
    () => DateTime.fromISO(selectedEvent.end),
    [selectedEvent.end],
  );
  const durationSec = useMemo(() => {
    const d = endDT.diff(startDT, 'seconds').seconds;
    return Number.isFinite(d) ? Math.max(0, d) : 0;
  }, [startDT, endDT]);
  const zone: string | Zone = startDT.zone ?? endDT.zone;

  const onStartDateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newDateStr = e.target.value; // 'yyyy-MM-dd'
      const merged = mergeDateAndTime(
        newDateStr,
        startDT.toFormat('HH:mm'),
        zone,
      );
      const snapped = roundToStep(merged);
      const newEnd = snapped.plus({
        seconds: Math.max(durationSec, STEP_SECONDS),
      });

      handleTimeChange('start', snapped.toISO());
      handleTimeChange('end', newEnd.toISO());
    },
    [startDT, durationSec, zone],
  );

  const onStartTimeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newTimeStr = e.target.value; // 'HH:mm'
      const merged = mergeDateAndTime(
        startDT.toFormat('yyyy-MM-dd'),
        newTimeStr,
        zone,
      );
      const snapped = roundToStep(merged);
      const newEnd = snapped.plus({
        seconds: Math.max(durationSec, STEP_SECONDS),
      });

      handleTimeChange('start', snapped.toISO());
      handleTimeChange('end', newEnd.toISO());
    },
    [startDT, durationSec, zone],
  );

  const onEndDateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newDateStr = e.target.value; // 'yyyy-MM-dd'
      const merged = mergeDateAndTime(
        newDateStr,
        endDT.toFormat('HH:mm'),
        zone,
      );
      let snapped = roundToStep(merged);

      if (snapped <= startDT) {
        snapped = roundToStep(startDT.plus({ seconds: STEP_SECONDS }));
      }
      handleTimeChange('end', snapped.toISO());
    },
    [endDT, startDT, zone],
  );

  const onEndTimeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newTimeStr = e.target.value; // 'HH:mm'
      const merged = mergeDateAndTime(
        endDT.toFormat('yyyy-MM-dd'),
        newTimeStr,
        zone,
      );
      let snapped = roundToStep(merged);

      if (snapped <= startDT) {
        snapped = roundToStep(startDT.plus({ seconds: STEP_SECONDS }));
      }
      handleTimeChange('end', snapped.toISO());
    },
    [endDT, startDT, zone],
  );

  const [navDate, setNavDate] = useState(DateTime.local().startOf('day'));
  const [fcViewType, setFcViewType] = useState('dayGridMonth');

  const handleNavigatorChange = (nextDay: DateTime) => {
    setNavDate(nextDay); // keep picker controlled

    const api = calendarRef.current?.getApi();
    if (!api) return;

    // Respect current FC view:
    // - Month: stay in month, just go to that day’s month
    // - Week/Day: keep the view, go to that day
    switch (fcViewType) {
      case 'dayGridMonth':
        api.changeView('dayGridMonth', nextDay.toJSDate()); // snap to that month
        break;
      case 'timeGridWeek':
      case 'timeGridDay':
      default:
        api.gotoDate(nextDay.toJSDate());
        break;
    }
  };

  const SIDEBAR_W = 340;

  return (
    <Container maxWidth={false}>
      <GatewayLoadingIndicator
        isLoading={
          isLoadingOccupancyEvents ||
          isFetchingOccupancyEvents ||
          isLoadingBuildings ||
          isFetchingBuildings ||
          isLoadingSubscriptions ||
          isFetchingSubscriptions ||
          !account
        }
      />
      <Box
        sx={{
          mb: 2,
          display: 'grid',
          gap: 2,
          // One column on md and below; two columns from lg+
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: '1fr',
            lg: `${SIDEBAR_W}px 1fr`,
          },
          alignItems: 'start',
        }}
      >
        {/* Sidebar — hidden on md and below */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
            position: 'sticky',
            top: 16,
            width: SIDEBAR_W,
          }}
        >
          <Stack spacing={2}>
            <StaticDateRangeNavigator
              value={navDate}
              onChange={handleNavigatorChange}
              currentView={fcViewType}
            />
            <ViewFilterSelector
              buildings={buildingsWithSubs}
              visibleBuildings={initializedViewFilter.buildings}
              visibleSubs={initializedViewFilter.subs}
              onToggleBuilding={toggleViewBuilding}
              onToggleSub={toggleViewSub}
            />
          </Stack>
        </Box>

        {/* Main */}
        <Card
          className="fc-light-theme"
          sx={{ height: '85vh', p: 2, minWidth: 0 }}
        >
          <FullCalendar
            ref={calendarRef}
            datesSet={(arg: DatesSetArg) => {
              // Use the view's currentStart as the anchor; normalize to start of day
              const anchor = DateTime.fromJSDate(
                (arg.view && arg.view.currentStart) || arg.start,
              ).startOf('day');

              // Update view type and nav date only when actually changed
              setFcViewType((prev) =>
                prev === arg.view.type ? prev : arg.view.type,
              );
              setNavDate((prev) =>
                prev && prev.hasSame(anchor, 'day') ? prev : anchor,
              );
            }}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
              bootstrapPlugin,
            ]}
            eventClassNames={(arg) =>
              arg.view.type === 'dayGridMonth' ? ['month-bar'] : []
            }
            initialView="dayGridMonth"
            themeSystem="standard"
            locales={[daLocale]}
            nowIndicator
            firstDay={1}
            locale="da"
            height="100%"
            selectable
            selectMirror={false}
            select={handleDateSelect}
            eventClick={handleExistingEventClick}
            slotEventOverlap={false}
            events={visibleEvents}
            eventDidMount={handleEventDidMount}
            eventWillUnmount={handleEventWillUnmount}
            headerToolbar={{
              center: 'title',
              start: 'prev,next,today',
              end: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            customButtons={{
              today: { text: t('today'), click: handleTodayButtonClick },
            }}
            titleFormat={{ month: 'long', year: 'numeric' }}
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            eventContent={(arg: EventContentArg) => {
              const extendedProps = arg.event
                .extendedProps as CalendarExtendedProps;
              const isBuilding = extendedProps.scope === 'building';

              return (
                <Stack sx={{ minWidth: 0, overflow: 'hidden' }}>
                  <ListItemText
                    sx={{ m: 1, minWidth: 0, overflow: 'hidden' }}
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        noWrap
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {arg.event.title}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        fontSize="11px"
                        noWrap
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {arg.timeText !== '00.00' ? arg.timeText + ' • ' : ''}
                        {isBuilding
                          ? extendedProps.buildingName
                          : extendedProps.subscriptionDisplayName}
                      </Typography>
                    }
                  />
                </Stack>
              );
            }}
            views={{
              dayGridMonth: { dayHeaderFormat: { weekday: 'long' } },
              timeGridWeek: {
                dayHeaderContent: (args) => {
                  const date = args.date;
                  return (
                    <div style={{ textAlign: 'center', marginBottom: 8 }}>
                      {DateTime.fromJSDate(date).toFormat('dd')}
                      <div>
                        {DateTime.fromJSDate(date)
                          .setLocale('da')
                          .toFormat('ccc')}
                      </div>
                    </div>
                  );
                },
              },

              listWeek: {
                dayHeaderFormat: {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                },
              },

              timeGridDay: {
                dayHeaderFormat: {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                },
              },
            }}
            allDaySlot={false}
          />
        </Card>
      </Box>

      {/* CREATE / DETAILS DIALOG */}
      <Dialog
        open={Boolean(anchorEl)}
        onClose={handlePopoverClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {isEventClick ? selectedEvent.title || 'Begivenhed' : 'Ny begivenhed'}
          <IconButton
            aria-label="close"
            onClick={handlePopoverClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small"
            fullWidth
            variant="filled"
            label={t('addTitle')}
            type="text"
            value={selectedEvent.title}
            error={selectedEvent.title.trim() === ''}
            onChange={(e) =>
              setSelectedEvent((p) => ({ ...p, title: e.target.value }))
            }
          />

          {/* --- Outlook-style Date + Time fields --- */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mt: 2,
            }}
          >
            {/* Start Dato */}

            <TextField
              size="small"
              label={t('startDate')}
              variant="filled"
              type="date"
              fullWidth
              value={startDT.toFormat('yyyy-LL-dd')}
              error={endDT <= startDT || !endDT}
              onChange={onStartDateChange}
              onBlur={validateAndSetEventTimes}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(0.6)', // makes it visible on light background
                },
              }}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
            {/* Start Tid */}
            <TextField
              size="small"
              type="time"
              variant="filled"
              label={t('startTime')}
              fullWidth
              value={startDT.toFormat('HH:mm')}
              error={endDT <= startDT || !endDT}
              onChange={onStartTimeChange}
              onBlur={validateAndSetEventTimes}
              sx={{
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(0.6)', // makes it visible on light background
                },
              }}
              slotProps={{
                htmlInput: { step: STEP_SECONDS },
                inputLabel: { shrink: true },
              }}
            />

            {/* Slut Dato */}
            <TextField
              size="small"
              label={t('endDate')}
              variant="filled"
              type="date"
              fullWidth
              value={endDT.toFormat('yyyy-LL-dd')}
              error={endDT < startDT || !endDT}
              onChange={onEndDateChange}
              onBlur={validateAndSetEventTimes}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(0.6)', // makes it visible on light background
                },
              }}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
            {/* Slut Tid */}
            <TextField
              size="small"
              label={t('endTime')}
              variant="filled"
              type="time"
              fullWidth
              value={endDT.toFormat('HH:mm')}
              error={endDT < startDT || !endDT}
              onChange={onEndTimeChange}
              onBlur={validateAndSetEventTimes}
              sx={{
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(0.6)', // makes it visible on light background
                },
              }}
              slotProps={{
                htmlInput: { step: STEP_SECONDS },
                inputLabel: { shrink: true },
              }}
            />
          </Box>

          {/* Type (create + edit) */}
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={selectedEvent.type}
              onChange={handleTypeChange}
            >
              <FormControlLabel
                value="occupied"
                control={<Radio />}
                label={t('occupied')}
              />
              <FormControlLabel
                value="empty"
                control={<Radio />}
                label={t('empty')}
              />
            </RadioGroup>
          </FormControl>

          <EventDetails
            isEventClick={isEventClick}
            selectedEvent={selectedEvent}
            subsById={subsById}
          />

          {/* Targets (create only) */}
          {!isEventClick && (
            <TargetsSelector
              title={t('selectTargets')}
              buildings={buildingsWithSubs}
              selectedBuildings={targetSelections.buildings}
              selectedSubs={targetSelections.subs}
              onToggleBuilding={toggleBuilding}
              onToggleSub={toggleSub}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {/* Left: delete (only when editing an existing event) */}
            {isEventClick && (
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleDelete}
              >
                {t('removeEvent')}
              </Button>
            )}

            {/* Spacer pushes the next items to the right */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Right: cancel + create/update */}
            <Button variant="text" size="small" onClick={handlePopoverClose}>
              {t('cancel')}
            </Button>
            {!isEventClick ? (
              <Button
                variant="contained"
                size="small"
                onClick={handleCreate}
                disabled={selectedEvent.title.trim() === ''}
              >
                {t('create')}
              </Button>
            ) : (
              <Button variant="contained" size="small" onClick={handleUpdate}>
                {t('updateEvent')}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
